/**
 * useSession — Selector hook for a single WebSocket session.
 *
 * Reads session state from the global `activeSessionsStore` (two separate
 * selectors for high-frequency vs. low-frequency slices) and delegates all
 * actions to the `WebSocketPool` singleton.
 *
 * Performance notes:
 * - `sessions[key]` updates at streaming frequency (~50 Hz) — isolated selector.
 * - `connections[key]` updates only on reconnect events — isolated selector.
 * - Action functions are wrapped in `useMemo` with `sessionKey` as the sole
 *   dependency, so they never change identity across renders.
 * - `EMPTY_TIMELINE` is module-level to avoid allocating a new empty array
 *   on every render when the session slot does not yet exist in the store.
 */

import { useMemo } from 'react';
import { useActiveSessionsStore } from '../stores/activeSessionsStore';
import { useWebSocketPool } from '../providers/WebSocketPoolProvider';
import { IDLE_CONTEXT_COMPACTION } from '../types/session.types';
import type {
  CompactContextCapability,
  ContextCompactionState,
  TimelineItem,
  SessionStatus,
} from '../types/session.types';
import type { MemoryUsage } from '../types/websocket.types';
import type { ReconnectInfo } from '../types/session.types';
import type { ToolsConfig, SendMessageOptions } from '../types/websocket.types';
import type { LegalQuestion, Jurisdiction, FondementJuridique } from '../components/jurisprudence/types';
import type { RedactionPlanResponsePayload, SkillChangeResponse } from '@hector/shared-types';

type WebRedactionSources = NonNullable<RedactionPlanResponsePayload['sources']>;

// ============================================================================
// Stable empty constants — defined OUTSIDE the hook to prevent re-renders
// when the session slot does not yet exist in the store.
// ============================================================================

const EMPTY_TIMELINE: TimelineItem[] = [];

// ============================================================================
// Return type
// ============================================================================

export interface UseSessionReturn {
  // ---- Streaming state -------------------------------------------------------
  /** Chronological list of streaming items for the current exchange. */
  timeline: TimelineItem[];
  /** True while the agent is actively streaming a response. */
  isStreaming: boolean;
  /**
   * True while the message has been sent but the first delta has not arrived.
   * Replaces the old `isWaitingForAgent` flag.
   */
  isWaitingForAgent: boolean;
  /** Latest context-window usage snapshot. */
  memoryUsage: MemoryUsage | null;
  /** Dernière réponse d'une socket de cette session sur la compaction manuelle (`unknown` sans session). */
  compactContextCapability: CompactContextCapability;
  /** Compaction manuelle : `running` bloque l'envoi, jamais `status`. */
  compaction: ContextCompactionState;
  /** Fine-grained session lifecycle status. */
  status: SessionStatus | null;
  /** Supabase conversation UUID (null before the first exchange completes). */
  conversationId: string | null;
  /**
   * `Date.now()` origin of the current agent turn (null when idle). Origin of the
   * feather timer — lives in the session so it stays correct across conversation
   * switches (a background turn shows real elapsed time, not time-since-open).
   */
  turnStartedAt: number | null;

  // ---- Connection state ------------------------------------------------------
  /** True when the WebSocket is open and authenticated. */
  isConnected: boolean;
  /** Non-null while automatic reconnection is in progress. */
  reconnectInfo: ReconnectInfo | null;
  /** True once all reconnection attempts have been exhausted. */
  reconnectFailed: boolean;

  // ---- Actions ---------------------------------------------------------------
  /**
   * Send a user message on this session.
   * Reconnects automatically if the WebSocket is currently down.
   */
  sendMessage(
    content: string,
    toolsConfig: ToolsConfig,
    options: SendMessageOptions,
  ): Promise<void>;

  /** Interrupt the current stream (sends `user_interrupt` to the backend). */
  cancelStream(): void;

  /**
   * Send the user's validated jurisprudence questions back to the backend,
   * unblocking the paused agent.
   */
  sendJurisprudenceResponse(
    jobId: string,
    questions: LegalQuestion[],
    jurisdictions: Jurisdiction[],
    fondements: FondementJuridique[],
  ): void;

  /** Cancel the pending jurisprudence validation form. */
  sendJurisprudenceCancellation(jobId: string): void;

  /** Send the user's explicit decision for a destructive Analyse avancée reset. */
  sendCaseWikiResetResponse(payload: {
    caseId: string;
    accepted: boolean;
    requestId: string;
    caseWikiContextUserInputs?: string;
  }): boolean;

  /** Send the user's consent/refusal for a destructive piece deletion. */
  sendDeletePiecesConfirmationResponse(payload: {
    caseId: string;
    accepted: boolean;
    requestId: string;
  }): boolean;

  /** Send the user's decision for an inline personal-skill mutation proposal. */
  sendSkillChangeResponse(payload: Omit<SkillChangeResponse, 'type'>): boolean;

  /** Soumet manuellement la carte du plan ; la transition UI attend l'accusé backend. */
  sendRedactionPlanResponse(
    requestId: string,
    additionalInstructions: string | undefined,
    skipJurisprudence?: boolean,
    overrides?: { actId?: string; juridictions?: string[]; sources?: WebRedactionSources },
  ): boolean;

  /** Annulation de la carte de validation du plan (clic Annuler). */
  sendRedactionPlanCancellation(requestId: string): boolean;
  /** Réarme l'échéance backend avec le dernier état effectif de la carte. */
  sendRedactionPlanDraftUpdate(
    requestId: string,
    snapshot: import('@hector/shared-types').RedactionPlanDraftSnapshot,
  ): boolean;

  /**
   * Signal le SSE canvas a terminé (end-of-stream). Si le verdict backend est
   * déjà bufferisé sur le progress item, ça flippe la carte chat vers la carte
   * results (finalisation). Sinon ça marque la phase 'complete' en attendant.
   *
   * Émis par `redactionStreamHub` à la fin d'une écoute de rédaction (#857),
   * via le notificateur que le pool lui pose.
   */
  notifyRedactionSseComplete(jobId: string, status?: 'complete' | 'error' | 'cancelled'): void;

  /**
   * Manually retry after reconnection exhaustion.
   * Clears failure state and restarts the reconnection loop.
   */
  retryConnection(): void;
}

// ============================================================================
// Hook
// ============================================================================

/**
 * Selector hook for a single WebSocket session in the pool.
 *
 * @param sessionKey - The opaque session identifier returned by
 *   `WebSocketPool.openSession()`. If the session does not exist in the store
 *   yet, all state fields return empty/null defaults and actions are no-ops.
 */
export function useSession(sessionKey: string): UseSessionReturn {
  const pool = useWebSocketPool();

  // ---- Selector 1: high-frequency session slice ------------------------------
  const session = useActiveSessionsStore(
    (state) => state.sessions[sessionKey],
  );

  // ---- Selector 2: low-frequency connection slice ----------------------------
  const connection = useActiveSessionsStore(
    (state) => state.connections[sessionKey],
  );

  // ---- Derived scalars from session ------------------------------------------
  const timeline = session?.timeline ?? EMPTY_TIMELINE;
  const status = session?.status ?? null;
  const conversationId = session?.conversationId ?? null;
  const turnStartedAt = session?.turnStartedAt ?? null;
  const memoryUsage = session?.memoryUsage ?? null;
  const compactContextCapability = session?.compactContextCapability ?? 'unknown';
  const compaction = session?.compaction ?? IDLE_CONTEXT_COMPACTION;

  // Streaming-derived booleans exposed to the component layer.
  const isStreaming = status === 'streaming';
  const isWaitingForAgent = status === 'connecting';

  // ---- Derived from connection ------------------------------------------------
  const isConnected = connection?.isConnected ?? false;
  const reconnectInfo = connection?.reconnectInfo ?? null;
  const reconnectFailed = connection?.reconnectFailed ?? false;

  // ---- Actions — wrapped in useMemo for stable identity ----------------------
  // Only `sessionKey` is a dependency because:
  //   - All actions reference only the pool singleton and the store method, which
  //     are themselves stable across renders.
  //   - Rebuilding closures on every render would trigger downstream re-renders
  //     in components that use these as effect dependencies or callback props.
  const actions = useMemo(() => ({
      sendMessage: (
        content: string,
        toolsConfig: ToolsConfig,
        options: SendMessageOptions,
      ): Promise<void> => {
        return pool.sendMessage(sessionKey, content, toolsConfig, options);
      },

      cancelStream: (): void => {
        pool.cancelStream(sessionKey);
      },

      sendJurisprudenceResponse: (
        jobId: string,
        questions: LegalQuestion[],
        jurisdictions: Jurisdiction[],
        fondements: FondementJuridique[],
      ): void => {
        pool.sendJurisprudenceResponse(sessionKey, jobId, questions, jurisdictions, fondements);
      },

      sendJurisprudenceCancellation: (jobId: string): void => {
        pool.sendJurisprudenceCancellation(sessionKey, jobId);
      },

      sendCaseWikiResetResponse: (payload: {
        caseId: string;
        accepted: boolean;
        requestId: string;
        caseWikiContextUserInputs?: string;
      }): boolean => {
        return pool.sendCaseWikiResetResponse(sessionKey, payload);
      },

      sendDeletePiecesConfirmationResponse: (payload: {
        caseId: string;
        accepted: boolean;
        requestId: string;
      }): boolean => {
        return pool.sendDeletePiecesConfirmationResponse(sessionKey, payload);
      },

      sendRedactionPlanResponse: (
        requestId: string,
        additionalInstructions: string | undefined,
        skipJurisprudence?: boolean,
        overrides?: { actId?: string; juridictions?: string[]; sources?: WebRedactionSources },
      ): boolean => {
        return pool.sendRedactionPlanResponse(sessionKey, {
          requestId,
          accepted: true,
          ...(additionalInstructions !== undefined ? { additional_instructions: additionalInstructions } : {}),
          ...(skipJurisprudence !== undefined ? { skip_jurisprudence: skipJurisprudence } : {}),
          ...(overrides?.actId ? { actId: overrides.actId } : {}),
          ...(overrides?.juridictions && overrides.juridictions.length > 0 ? { jurisdictions: overrides.juridictions } : {}),
          ...(overrides?.sources ? { sources: overrides.sources } : {}),
        });
      },

      sendRedactionPlanCancellation: (requestId: string): boolean => {
        return pool.sendRedactionPlanCancellation(sessionKey, requestId);
      },

      sendRedactionPlanDraftUpdate: (
        requestId: string,
        snapshot: import('@hector/shared-types').RedactionPlanDraftSnapshot,
      ): boolean => {
        return pool.sendRedactionPlanDraftUpdate(sessionKey, requestId, snapshot);
      },

      notifyRedactionSseComplete: (jobId: string, status: 'complete' | 'error' | 'cancelled' = 'complete'): void => {
        pool.notifyRedactionSseComplete(sessionKey, jobId, status);
      },

      sendSkillChangeResponse: (payload: Omit<SkillChangeResponse, 'type'>): boolean => {
        return pool.sendSkillChangeResponse(sessionKey, payload);
      },

      retryConnection: (): void => {
        pool.retryConnection(sessionKey);
      },
  }), [sessionKey, pool]);

  return {
    // State
    timeline,
    isStreaming,
    isWaitingForAgent,
    memoryUsage,
    compactContextCapability,
    compaction,
    status,
    conversationId,
    turnStartedAt,
    // Connection
    isConnected,
    reconnectInfo,
    reconnectFailed,
    // Actions
    ...actions,
  };
}
